// client/src/components/moonbeams/RescheduleModal.js
import { useState, useEffect } from 'react';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Generate days 1-31
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Generate time options (every 30 minutes)
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    const value = `${hour}:${minute}`;
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    const label = `${displayHour}:${minute.padStart(2, '0')} ${ampm}`;
    TIME_OPTIONS.push({ value, label });
  }
}

/**
 * RescheduleModal - Modal for rescheduling one or all pending recipients
 * 
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   onSave: (data) => Promise<void> - called with reschedule data
 *   mode: 'single' | 'all'
 *   deliveryMode: 'scheduled' | 'birthday'
 *   recipient: object (for single mode) - { recipientname, email, scheduledsendat, birthmonth, birthdate, birthdaytime, timezone }
 *   recipientCount: number (for all mode) - how many pending recipients
 */
export default function RescheduleModal({
  isOpen,
  onClose,
  onSave,
  mode = 'single',
  deliveryMode = 'scheduled',
  recipient = null,
  recipientCount = 0,
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Scheduled mode state
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');

  // Calendar state
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calYear, setCalYear] = useState(now.getFullYear());

  // Birthday mode state
  const [birthMonth, setBirthMonth] = useState(1);
  const [birthDay, setBirthDay] = useState(1);
  const [birthTime, setBirthTime] = useState('09:00');

  // Initialize form values when modal opens or recipient changes
  useEffect(() => {
    if (!isOpen) return;

    setError('');
    setShowCustom(false);

    if (deliveryMode === 'birthday') {
      if (recipient && mode === 'single') {
        setBirthMonth(recipient.birthmonth || 1);
        setBirthDay(recipient.birthdate || 1);
        setBirthTime(recipient.birthdaytime || '09:00');
      } else {
        // For "all" mode, just set defaults
        setBirthTime('09:00');
      }
    } else {
      // Scheduled mode - initialize calendar and time from existing schedule
      if (recipient && recipient.scheduledsendat && mode === 'single') {
        const scheduled = new Date(recipient.scheduledsendat);
        const year = scheduled.getFullYear();
        const month = (scheduled.getMonth() + 1).toString().padStart(2, '0');
        const day = scheduled.getDate().toString().padStart(2, '0');
        setCustomDate(`${year}-${month}-${day}`);
        setCalMonth(scheduled.getMonth());
        setCalYear(scheduled.getFullYear());
        
        const hours = scheduled.getHours().toString().padStart(2, '0');
        const minutes = scheduled.getMinutes().toString().padStart(2, '0');
        setCustomTime(`${hours}:${minutes}`);
      } else {
        // Default to tomorrow at 9am
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setCustomDate('');
        setCustomTime('09:00');
        setCalMonth(now.getMonth());
        setCalYear(now.getFullYear());
      }
    }
  }, [isOpen, recipient, mode, deliveryMode]);

  if (!isOpen) return null;

  // ─── Quick Options (for scheduled mode) ─────────────────────────────────────

  const tomorrowMorning = new Date(today);
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1);
  tomorrowMorning.setHours(8, 0, 0, 0);

  const isBefore1PM = now.getHours() < 13;
  const afternoonDate = new Date(today);
  if (isBefore1PM) {
    afternoonDate.setHours(13, 0, 0, 0);
  } else {
    afternoonDate.setDate(afternoonDate.getDate() + 1);
    afternoonDate.setHours(13, 0, 0, 0);
  }

  const nextMonday = new Date(today);
  const dayOfWeek = nextMonday.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 7 : 8 - dayOfWeek;
  nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
  nextMonday.setHours(8, 0, 0, 0);

  const formatQuickDate = (d) => {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const toLocalDatetimeString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const selectQuickOption = async (d) => {
    setError('');
    setSaving(true);
    try {
      const scheduledLocalTime = toLocalDatetimeString(d);
      await onSave({
        scheduledLocalTime,
        timezone: recipient?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onClose();
    } catch (err) {
      setError(err.data?.error || err.message || 'Failed to reschedule');
    } finally {
      setSaving(false);
    }
  };

  // ─── Calendar helpers ───────────────────────────────────────────────────────

  const calDaysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const calFirstDay = new Date(calYear, calMonth, 1).getDay();
  const calMonthName = new Date(calYear, calMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const selectCalDay = (day) => {
    const m = String(calMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    setCustomDate(`${calYear}-${m}-${d}`);
    setError('');
  };

  const isPastDay = (day) => {
    const d = new Date(calYear, calMonth, day);
    return d < today;
  };

  const selectedCalDay = customDate ? parseInt(customDate.split('-')[2]) : null;
  const selectedCalMonth = customDate ? parseInt(customDate.split('-')[1]) - 1 : null;
  const selectedCalYear = customDate ? parseInt(customDate.split('-')[0]) : null;

  // Check if selected date is today
  const isToday = customDate && (() => {
    const selected = new Date(customDate);
    return selected.getFullYear() === now.getFullYear() &&
           selected.getMonth() === now.getMonth() &&
           selected.getDate() === now.getDate();
  })();

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'your timezone';

  // ─── Save handlers ──────────────────────────────────────────────────────────

  const handleCustomConfirm = async () => {
    setError('');

    if (!customDate || !customTime) {
      setError('Please select a date and time');
      return;
    }

    // Validate the selected date/time is in the future
    const selectedDateTime = new Date(`${customDate}T${customTime}`);
    const nowWithBuffer = new Date();
    nowWithBuffer.setMinutes(nowWithBuffer.getMinutes() + 1);

    if (selectedDateTime <= nowWithBuffer) {
      setError('Please select a time in the future');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        scheduledLocalTime: `${customDate}T${customTime}`,
        timezone: recipient?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      onClose();
    } catch (err) {
      setError(err.data?.error || err.message || 'Failed to reschedule');
    } finally {
      setSaving(false);
    }
  };

  const handleBirthdaySave = async () => {
    setError('');
    setSaving(true);

    try {
      let data = {};

      if (mode === 'single') {
        data = {
          birthmonth: birthMonth,
          birthdate: birthDay,
          birthdaytime: birthTime,
        };
      } else {
        // All mode for birthday only updates the time
        data = {
          birthdaytime: birthTime,
        };
      }

      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.data?.error || err.message || 'Failed to reschedule');
    } finally {
      setSaving(false);
    }
  };

  // ─── Title ──────────────────────────────────────────────────────────────────

  const getTitle = () => {
    if (mode === 'all') {
      return `Reschedule All (${recipientCount})`;
    }
    const name = recipient?.recipientname || recipient?.firstname || recipient?.email || 'Recipient';
    return `Reschedule: ${name}`;
  };

  const getSubtitle = () => {
    if (deliveryMode === 'birthday') {
      return mode === 'all' ? 'Update delivery time for all pending' : 'Change birthday or delivery time';
    }
    return timezone;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-royal-900 border border-royal-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col sm:mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-royal-700/50 flex-shrink-0">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">{getTitle()}</h2>
            <p className="text-white/70 text-sm">{getSubtitle()}</p>
          </div>
          <button onClick={onClose} disabled={saving} className="text-white hover:text-gold transition-colors text-xl p-2 disabled:opacity-50">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {deliveryMode === 'birthday' ? (
            /* ─── BIRTHDAY MODE ─────────────────────────────────────────────── */
            <div className="space-y-4">
              {mode === 'single' ? (
                <>
                  <div>
                    <label className="block text-white text-sm mb-1.5">Birthday Month</label>
                    <select
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold transition-colors"
                    >
                      {MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-white text-sm mb-1.5">Birthday Day</label>
                    <select
                      value={birthDay}
                      onChange={(e) => setBirthDay(parseInt(e.target.value))}
                      className="w-full px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold transition-colors"
                    >
                      {DAYS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <p className="text-royal-100 text-sm">
                  This will update the send time for all {recipientCount} pending recipient{recipientCount !== 1 ? 's' : ''}.
                  Each person's birthday date will remain unchanged.
                </p>
              )}

              <div>
                <label className="block text-white text-sm mb-1.5">Send Time</label>
                <select
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="w-full px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold transition-colors"
                >
                  {TIME_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p className="text-royal-100 text-xs mt-1.5">
                  Time is in the recipient's local timezone
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-4">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBirthdaySave}
                  disabled={saving}
                  className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : !showCustom ? (
            /* ─── SCHEDULED MODE: QUICK OPTIONS ─────────────────────────────── */
            <div className="space-y-2">
              {mode === 'all' && (
                <p className="text-royal-100 text-sm mb-3">
                  This will reschedule all {recipientCount} pending recipient{recipientCount !== 1 ? 's' : ''} to the new date and time.
                </p>
              )}

              {/* Quick option: Tomorrow morning */}
              <button
                onClick={() => selectQuickOption(tomorrowMorning)}
                disabled={saving}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all disabled:opacity-50"
              >
                <span className="text-white text-base">Tomorrow morning</span>
                <span className="text-white/70 text-sm">{formatQuickDate(tomorrowMorning)}</span>
              </button>

              {/* Quick option: This/Tomorrow afternoon */}
              <button
                onClick={() => selectQuickOption(afternoonDate)}
                disabled={saving}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all disabled:opacity-50"
              >
                <span className="text-white text-base">{isBefore1PM ? 'This afternoon' : 'Tomorrow afternoon'}</span>
                <span className="text-white/70 text-sm">{formatQuickDate(afternoonDate)}</span>
              </button>

              {/* Quick option: Monday morning */}
              <button
                onClick={() => selectQuickOption(nextMonday)}
                disabled={saving}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-lg border border-royal-700 bg-royal-800/40 hover:border-gold/50 hover:bg-royal-800/60 transition-all disabled:opacity-50"
              >
                <span className="text-white text-base">Monday morning</span>
                <span className="text-white/70 text-sm">{formatQuickDate(nextMonday)}</span>
              </button>

              {/* Custom option */}
              <button
                onClick={() => setShowCustom(true)}
                disabled={saving}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border border-royal-600 bg-royal-900/40 hover:border-gold/50 transition-all mt-3 disabled:opacity-50"
              >
                <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-gold text-base font-medium">Pick date & time</span>
              </button>
            </div>
          ) : (
            /* ─── SCHEDULED MODE: CUSTOM DATE/TIME ──────────────────────────── */
            <div className="space-y-4">
              {mode === 'all' && (
                <p className="text-royal-100 text-sm">
                  This will reschedule all {recipientCount} pending recipient{recipientCount !== 1 ? 's' : ''} to the new date and time.
                </p>
              )}

              {/* Calendar */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => {
                      if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
                      else setCalMonth(calMonth - 1);
                    }}
                    className="text-white hover:text-gold transition-colors p-1"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-white font-medium text-base">{calMonthName}</span>
                  <button
                    onClick={() => {
                      if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
                      else setCalMonth(calMonth + 1);
                    }}
                    className="text-white hover:text-gold transition-colors p-1"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div key={d} className="text-white/50 text-xs py-1">{d}</div>
                  ))}
                  {Array.from({ length: calFirstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                  {Array.from({ length: calDaysInMonth }, (_, i) => i + 1).map((day) => {
                    const past = isPastDay(day);
                    const isSelected = selectedCalDay === day && selectedCalMonth === calMonth && selectedCalYear === calYear;
                    return (
                      <button
                        key={day}
                        onClick={() => !past && selectCalDay(day)}
                        disabled={past}
                        className={`py-1.5 rounded-lg text-sm transition-colors ${
                          past ? 'text-white/20 cursor-not-allowed'
                            : isSelected ? 'bg-gold text-royal-900 font-bold'
                            : 'text-white hover:bg-royal-800 hover:text-gold'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Date + Time display */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white/70 text-sm mb-1">Date</label>
                  <div className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base">
                    {customDate
                      ? new Date(customDate + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Select a date'}
                  </div>
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-1">Time</label>
                  <select
                    value={customTime}
                    onChange={(e) => { setCustomTime(e.target.value); setError(''); }}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold/50 transition-colors"
                  >
                    {TIME_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Today warning */}
              {isToday && (
                <p className="text-amber-400/80 text-sm px-1">
                  ⚠️ Rescheduling for today — make sure the time is still in the future.
                </p>
              )}

              {/* Buttons */}
              <div className="flex justify-between pt-2">
                <button
                  onClick={() => { setShowCustom(false); setError(''); }}
                  disabled={saving}
                  className="px-4 py-2.5 text-white text-base hover:text-gold transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleCustomConfirm}
                  disabled={!customDate || saving}
                  className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Reschedule'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}